export type ScannerKeySource = "key" | "code" | "legacy-key-code";

export type ScannerKeyResolution =
  | {
      status: "resolved";
      key: string;
      source: ScannerKeySource;
    }
  | {
      status: "unresolved";
      source: "none";
    };

export interface ScannerKeyboardEvent {
  key?: string;
  code?: string;
  keyCode?: number;
  which?: number;
}

const CODE_KEY_MAP: Record<string, string> = {
  Semicolon: ";",
  Minus: "-",
  NumpadSubtract: "-",
  Enter: "Enter",
  NumpadEnter: "Enter",
  Tab: "Tab",
  ShiftLeft: "Shift",
  ShiftRight: "Shift",
  CapsLock: "CapsLock",
  ControlLeft: "Control",
  ControlRight: "Control",
  AltLeft: "Alt",
  AltRight: "Alt",
  MetaLeft: "Meta",
  MetaRight: "Meta",
  Escape: "Escape",
  Backspace: "Backspace",
  Delete: "Delete",
};

const LEGACY_KEY_MAP: Record<number, string> = {
  9: "Tab",
  13: "Enter",
  16: "Shift",
  17: "Control",
  18: "Alt",
  20: "CapsLock",
  27: "Escape",
  59: ";",
  109: "-",
  173: "-",
  186: ";",
  189: "-",
};

const UNUSABLE_EVENT_KEYS = new Set(["Unidentified", "Process", "Dead"]);

const resolvedKey = (
  key: string,
  source: ScannerKeySource
): ScannerKeyResolution => ({ status: "resolved", key, source });

const resolveKeyFromCode = (code = ""): ScannerKeyResolution => {
  if (/^Key[A-Z]$/.test(code)) return resolvedKey(code.slice(3), "code");
  if (/^Digit[0-9]$/.test(code)) return resolvedKey(code.slice(5), "code");
  if (/^Numpad[0-9]$/.test(code)) return resolvedKey(code.slice(6), "code");
  const mappedKey = CODE_KEY_MAP[code];
  return mappedKey
    ? resolvedKey(mappedKey, "code")
    : { status: "unresolved", source: "none" };
};

const resolveLegacyKey = (keyCode: number): ScannerKeyResolution => {
  if (keyCode >= 65 && keyCode <= 90) {
    return resolvedKey(String.fromCharCode(keyCode), "legacy-key-code");
  }
  if (keyCode >= 48 && keyCode <= 57) {
    return resolvedKey(String.fromCharCode(keyCode), "legacy-key-code");
  }
  if (keyCode >= 96 && keyCode <= 105) {
    return resolvedKey(String(keyCode - 96), "legacy-key-code");
  }
  const mappedKey = LEGACY_KEY_MAP[keyCode];
  return mappedKey
    ? resolvedKey(mappedKey, "legacy-key-code")
    : { status: "unresolved", source: "none" };
};

export const resolveScannerKey = (
  event: ScannerKeyboardEvent
): ScannerKeyResolution => {
  if (
    typeof event.key === "string" &&
    event.key.length > 0 &&
    !UNUSABLE_EVENT_KEYS.has(event.key)
  ) {
    return resolvedKey(event.key, "key");
  }
  const legacyResolution = resolveLegacyKey(event.keyCode || event.which || 0);
  if (legacyResolution.status === "resolved") return legacyResolution;
  return resolveKeyFromCode(event.code);
};
